import { Fragment, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import {
    listarProyectos,
    filtrarProyectos,
    setFiltrosProyectos,
    limpiarFiltrosProyectos
} from '../../actions/proyectos';
import { listarClientes } from '../../actions/clientes';

import NuevoProyectoModal from './modals/NuevoProyectoModal';
import EditarProyectoModal from './modals/EditarProyectoModal';
import CambiarStatusProyectoModal from './modals/CambiarStatusProyectoModal';
import EliminarProyectoModal from './modals/EliminarProyectoModal';

const opcionesStatus = [
    { value: 1, label: 'Activo' },
    { value: 2, label: 'En proceso' },
    { value: 3, label: 'Pausado' },
    { value: 4, label: 'Terminado' },
    { value: 5, label: 'Cancelado' },
    { value: 6, label: 'Eliminado' }
];

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const renderBadgeStatus = (status) => {
    const s = Number(status);
    if (s === 1) return <span className="badge bg-success">Activo</span>;
    if (s === 2) return <span className="badge bg-info text-dark">En proceso</span>;
    if (s === 3) return <span className="badge bg-warning text-dark">Pausado</span>;
    if (s === 4) return <span className="badge bg-primary">Terminado</span>;
    if (s === 5) return <span className="badge bg-danger">Cancelado</span>;
    if (s === 6) return <span className="badge bg-secondary">Eliminado</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const Proyectos = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(editarBreadcrumb([{ activo: true, nombre: 'Proyectos', path: '/proyectos' }]));
    }, [dispatch]);

    const { filtrado, filtro_activo, filtros } = useSelector(state => state.proyectos);
    const { listado: listado_clientes } = useSelector(state => state.clientes);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [modalFiltros, setModalFiltros] = useState(false);
    const [modalNuevo, setModalNuevo] = useState(false);
    const [proyectoEditar, setProyectoEditar] = useState(null);
    const [proyectoEstado, setProyectoEstado] = useState(null);
    const [proyectoEliminar, setProyectoEliminar] = useState(null);

    const [clienteSel, setClienteSel] = useState(null);
    const [nombreFiltro, setNombreFiltro] = useState('');
    const [statusSel, setStatusSel] = useState(null);

    useEffect(() => {
        dispatch(listarProyectos({}));
        dispatch(listarClientes());
    }, [dispatch]);

    useEffect(() => {
        if (!filtro_activo) return;

        setNombreFiltro(filtros?.nombre || '');

        if (listado_clientes?.length && filtros?.id_cliente) {
            const match = listado_clientes.find(c => (c?._id + '') === (filtros.id_cliente + ''));
            if (match) setClienteSel({ value: match._id, label: match.nombre });
        }

        if (filtros?.status) {
            const match = opcionesStatus.find(s => (s.value + '') === (filtros.status + ''));
            if (match) setStatusSel(match);
        }
    }, [filtro_activo, filtros, listado_clientes]);

    const opcionesClientes = useMemo(() => {
        return (listado_clientes || []).map(c => ({
            value: c._id,
            label: c.nombre
        }));
    }, [listado_clientes]);

    const columns = useMemo(() => ([
        {
            name: 'Cliente',
            selector: row => row?.cliente?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: 'Proyecto',
            selector: row => row?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: 'Dirección',
            selector: row => row?.direccion || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2
        },
        {
            name: 'Fecha inicio',
            selector: row => formatDate(row?.fecha_inicio_tentativa),
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Fecha fin',
            selector: row => formatDate(row?.fecha_fin_tentativa),
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Status',
            sortable: true,
            width: '140px',
            cell: row => renderBadgeStatus(row?.status)
        },
        {
            name: 'Opciones',
            width: '190px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Editar"
                        onClick={() => setProyectoEditar(row)}
                    >
                        <i className="fa fa-edit text-warning" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Cambiar status"
                        onClick={() => setProyectoEstado(row)}
                    >
                        <i className="fas fa-random text-info" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Ver detalle"
                        onClick={() => navigate(`/proyectos/detalle/${row._id}`)}
                    >
                        <i className="fas fa-eye text-primary" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Eliminar"
                        onClick={() => setProyectoEliminar(row)}
                    >
                        <i className="fas fa-trash text-danger" />
                    </button>
                </div>
            )
        }
    ]), [navigate]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarProyectos(val));
    };

    const abrirFiltros = () => setModalFiltros(true);
    const cerrarFiltros = () => setModalFiltros(false);

    const onBuscarAvanzado = async () => {
        const filtrosEnviar = {
            id_cliente: clienteSel?.value || '',
            nombre: nombreFiltro || '',
            status: statusSel?.value || ''
        };

        await dispatch(setFiltrosProyectos(filtrosEnviar));
        await dispatch(listarProyectos(filtrosEnviar));
        cerrarFiltros();
    };

    const onLimpiarAvanzado = async () => {
        setClienteSel(null);
        setNombreFiltro('');
        setStatusSel(null);
        setTextoFiltro('');

        await dispatch(limpiarFiltrosProyectos());
        await dispatch(listarProyectos({}));
        await dispatch(filtrarProyectos(''));
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Proyectos</h3>

                    <div className="d-flex justify-content-end mb-3">
                        <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                            <div style={{ minWidth: 280 }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar en la tabla..."
                                    value={textoFiltro}
                                    onChange={onChangeFiltroTabla}
                                />
                            </div>

                            <button type="button" className="btn btn-outline-secondary" onClick={abrirFiltros}>
                                <i className="feather icon-filter" /> Filtros avanzados
                            </button>

                            <button type="button" className="btn btn-primary" onClick={() => setModalNuevo(true)}>
                                <i className="feather icon-plus" /> Agregar proyecto
                            </button>
                        </div>
                    </div>

                    <div className="mt-2">
                        <DataTable
                            columns={columns}
                            data={filtrado || []}
                            pagination
                            paginationComponentOptions={paginationOptions}
                            highlightOnHover
                            striped
                            responsive
                            persistTableHead
                        />
                    </div>
                </div>
            </div>

            <Modal show={modalFiltros} onHide={cerrarFiltros} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Filtros avanzados</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="mb-3">
                        <div className="text-muted small mb-1">Cliente</div>
                        <Select
                            options={opcionesClientes}
                            value={clienteSel}
                            onChange={setClienteSel}
                            isClearable
                            placeholder="Cliente..."
                            noOptionsMessage={() => 'Sin resultados'}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="text-muted small mb-1">Nombre del proyecto</div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre del proyecto..."
                            value={nombreFiltro}
                            onChange={(e) => setNombreFiltro(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <div className="text-muted small mb-1">Status</div>
                        <Select
                            options={opcionesStatus}
                            value={statusSel}
                            onChange={setStatusSel}
                            isClearable
                            placeholder="Status..."
                            noOptionsMessage={() => 'Sin resultados'}
                        />
                    </div>

                    {filtro_activo && (
                        <div className="alert alert-info mb-0">
                            Hay filtros activos aplicados.
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button type="button" className="btn btn-primary" onClick={onBuscarAvanzado}>
                        Buscar
                    </button>

                    <button
                        type="button"
                        className="btn btn-warning"
                        onClick={onLimpiarAvanzado}
                        disabled={!filtro_activo && !clienteSel && !nombreFiltro && !statusSel}
                    >
                        Limpiar
                    </button>

                    <button type="button" className="btn btn-secondary" onClick={cerrarFiltros}>
                        Cerrar
                    </button>
                </Modal.Footer>
            </Modal>

            <NuevoProyectoModal
                show={modalNuevo}
                onHide={() => setModalNuevo(false)}
                onSuccess={() => setModalNuevo(false)}
                opcionesClientes={opcionesClientes}
            />

            <EditarProyectoModal
                show={!!proyectoEditar}
                idProyecto={proyectoEditar?._id || null}
                onHide={() => setProyectoEditar(null)}
                onSuccess={() => setProyectoEditar(null)}
                opcionesClientes={opcionesClientes}
            />

            <CambiarStatusProyectoModal
                show={!!proyectoEstado}
                proyecto={proyectoEstado}
                onHide={() => setProyectoEstado(null)}
                onSuccess={() => setProyectoEstado(null)}
            />

            <EliminarProyectoModal
                show={!!proyectoEliminar}
                idProyecto={proyectoEliminar?._id || null}
                onHide={() => setProyectoEliminar(null)}
            />
        </Fragment>
    );
};

export default Proyectos;
